import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Loader2, Flame, Lock, Brain, LogOut, Sparkles, User as UserIcon, Clock, BarChart3, Target, Gift, MoreHorizontal, MessagesSquare } from "lucide-react";
import { ConversationModal } from "./components/ConversationModal";
import { Switch } from "./components/ui/switch";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { VerdictCard } from "./components/VerdictCard";
import { AuthModal } from "./components/AuthModal";
import { BuyCreditsModal } from "./components/BuyCreditsModal";
import { HistoryModal } from "./components/HistoryModal";
import { PatternsModal } from "./components/PatternsModal";
import { DailyChallengeModal } from "./components/DailyChallengeModal";
import { ReferralModal } from "./components/ReferralModal";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./components/ui/dropdown-menu";
import { ShareCard } from "./components/ShareCard";
import * as htmlToImage from "html-to-image";
import { useAuth } from "./auth/AuthContext";
import { useLang } from "./i18n";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXAMPLES = [
  "Oi sumido 👀",
  "Sinto sua falta.",
  "Por que você curtiu a foto dela?",
  "A gente precisa conversar.",
  "Tá acordado?",
  "Sei que você tá ocupado, mas...",
];

const REL_OPTIONS = [
  "Crush / ainda não rolou nada",
  "Ficante",
  "Relacionamento sério",
  "Ex",
  "Amigos",
  "Pais",
  "Chefe",
  "Outro",
];

const STATUS_OPTIONS = [
  "Conversando bem",
  "Sumiu / ficou frio por um tempo",
  "Terminamos",
  "Nunca conversamos de verdade",
];

const VERDICT_EMOJI = { MANDA: "🟢", ESPERA: "🟡", NAO_MANDA: "🔴" };
const VERDICT_LABEL = { MANDA: "MANDA", ESPERA: "ESPERA", NAO_MANDA: "NÃO MANDA" };

const track = (event, meta = {}) => {
  axios.post(`${API}/track`, { event, meta }).catch(() => {});
};

function App() {
  const { user, logout, refreshUser } = useAuth();
  const { lang, toggle, t } = useLang();
  const REL_OPTIONS = t.rel_opts;
  const STATUS_OPTIONS = t.status_opts;
  const EXAMPLES = t.examples;
  const [mode, setMode] = useState("send");
  const [message, setMessage] = useState("");
  const [noFilter, setNoFilter] = useState(false);
  const [relationship, setRelationship] = useState("");
  const [relStatus, setRelStatus] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [replies, setReplies] = useState(null);
  const [reply3ing, setReply3ing] = useState(false);
  const [observations, setObservations] = useState(null);
  const [judging, setJudging] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPatterns, setShowPatterns] = useState(false);
  const [showDaily, setShowDaily] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [lowDismiss, setLowDismiss] = useState(false);
  const [showConvo, setShowConvo] = useState(false);
  const shareCardRef = useRef(null);
  const [buyReason, setBuyReason] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const runAnalyze = async (deep) => {
    const msg = message.trim();
    if (!msg) {
      toast.error(t.empty_msg);
      return;
    }
    if (!user) {
      setShowAuth(true);
      return;
    }
    deep ? setDeepLoading(true) : (setLoading(true), setResult(null), setSuggestion(null), setReplies(null), setObservations(null));
    track("message_analyzed", { mode, deep, length: msg.length });
    const payload = {
      message: msg,
      no_filter: noFilter,
      mode,
      deep,
      lang,
      relationship: relationship || null,
      relationship_status: relStatus || null,
    };
    if (deep) {
      payload.extra_note = extraNote || null;
    }
    try {
      const { data } = await axios.post(`${API}/analyze`, payload);
      setResult(data);
      track("verdict_generated", { verdict: data.verdict, deep });
      refreshUser();
    } catch (e) {
      if (e.response?.status === 401) {
        setShowAuth(true);
      } else if (e.response?.status === 402) {
        setBuyReason("limit");
        setShowBuy(true);
        refreshUser();
      } else {
        toast.error(t.analyze_fail);
      }
    } finally {
      deep ? setDeepLoading(false) : setLoading(false);
    }
  };

  const analyze = () => runAnalyze(false);
  const runDeep = () => runAnalyze(true);

  const pickExample = (ex) => {
    setMessage(ex);
    track("example_clicked", { example: ex });
  };

  const shareText = () => {
    if (!result) return "";
    return `Perguntei pro MANDO OU NÃO MANDO se eu devia mandar minha mensagem.\n\nVeredito:\n${VERDICT_EMOJI[result.verdict]} ${VERDICT_LABEL[result.verdict]}\n\n"${result.final_verdict}" 😂\n\nTesta a sua: ${window.location.origin}`;
  };

  const handleShare = async () => {
    track("share_clicked", { verdict: result?.verdict });
    // 1. Tenta gerar um card visual bonito (imagem)
    try {
      const node = shareCardRef.current;
      if (node) {
        const dataUrl = await htmlToImage.toPng(node, { pixelRatio: 1, cacheBust: true });
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "prapsy.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "prapsy", text: shareText() });
          return;
        }
        // Fallback: baixa a imagem
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "prapsy.png";
        a.click();
        toast.success(t.share_saved);
        return;
      }
    } catch (e) { console.error("share image failed, falling back to text:", e); }
    // 2. Fallback texto
    const text = shareText();
    if (navigator.share) {
      try { await navigator.share({ title: "prapsy", text }); } catch (e) { console.error("navigator.share failed:", e); }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success(t.share_copied);
    }
  };

  const handleCopy = async () => {
    track("result_copied", { verdict: result?.verdict });
    await navigator.clipboard.writeText(shareText());
    toast.success(t.copied);
  };

  const handleAnother = () => {
    track("analyze_another_clicked");
    setResult(null);
    setSuggestion(null);
    setReplies(null);
    setObservations(null);
    setMessage("");
    setRelationship("");
    setRelStatus("");
    setExtraNote("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReply3 = async () => {
    if (reply3ing) return;
    track("reply3_requested");
    setReply3ing(true);
    try {
      const { data } = await axios.post(`${API}/reply3`, {
        message: message.trim(), mode, lang,
        relationship: relationship || null, relationship_status: relStatus || null,
      });
      setReplies(data.replies);
    } catch (e) {
      toast.error(t.analyze_fail);
    } finally {
      setReply3ing(false);
    }
  };

  const handleJudgeMe = async () => {
    if (judging) return;
    track("judgeme_requested");
    setJudging(true);
    try {
      const { data } = await axios.post(`${API}/judge-me`, { message: message.trim(), mode, lang });
      setObservations(data.observations);
    } catch (e) {
      toast.error(t.analyze_fail);
    } finally {
      setJudging(false);
    }
  };

  const copyText = async (text) => {
    track("message_copied");
    await navigator.clipboard.writeText(text);
    toast.success(t.suggestion_copied);
  };

  const toggleFilter = (v) => {
    setNoFilter(v);
    if (v) track("no_filter_activated");
  };

  return (
    <div className="App grain min-h-screen w-full">
      <Toaster position="top-center" />

      {/* NAV */}
      <nav className="w-full border-b-[3px] border-black bg-[#F4F0E6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
            <img
              src="/assets/prapsy-icon.png"
              alt="prapsy"
              className="h-9 sm:h-11 w-auto"
            />
            <span className="font-bold tracking-tight text-2xl sm:text-3xl leading-none">
              <span className="text-[#16121f]">pra</span>
              <span className="text-[#FF3366]">psy</span>
            </span>
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="flex items-center gap-1 bg-white border-[2px] border-black rounded-full px-3 py-1.5 font-bold uppercase text-xs tracking-wide shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              {lang === "pt" ? "🇧🇷 PT" : "🇺🇸 EN"}
            </button>
            {user ? (
              <>
                {user.billing_enabled && (
                <button
                  data-testid="credits-counter"
                  onClick={() => { setBuyReason(null); setShowBuy(true); }}
                  className="flex items-center gap-1.5 bg-[#00FFA3] border-[2px] border-black rounded-full px-3 py-1.5 shadow-[3px_3px_0px_0px_#000] font-mono2 font-bold text-sm transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                >
                  <Sparkles size={15} strokeWidth={2.5} />
                  {user.credits}
                  {user.credits === 0 && user.free_available && <span className="hidden sm:inline">{t.credits_free}</span>}
                </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-menu-btn"
                      aria-label={t.nav_menu}
                      title={t.nav_menu}
                      className="border-[2px] border-black rounded-full p-1.5 bg-white shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                    >
                      <MoreHorizontal size={16} strokeWidth={2.5} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="border-[3px] border-black rounded-xl shadow-[6px_6px_0px_0px_#000] bg-[#F4F0E6] p-1.5 min-w-[196px]">
                    <DropdownMenuItem data-testid="nav-daily-btn" onClick={() => setShowDaily(true)} className="gap-2 font-bold rounded-lg cursor-pointer focus:bg-[#FFD500]">
                      <Target size={16} strokeWidth={2.5} /> {t.daily_title}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="nav-patterns-btn" onClick={() => setShowPatterns(true)} className="gap-2 font-bold rounded-lg cursor-pointer focus:bg-[#00FFA3]">
                      <BarChart3 size={16} strokeWidth={2.5} /> {t.pat_title}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="nav-history-btn" onClick={() => setShowHistory(true)} className="gap-2 font-bold rounded-lg cursor-pointer focus:bg-white">
                      <Clock size={16} strokeWidth={2.5} /> {t.nav_history}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="nav-referral-btn" onClick={() => setShowReferral(true)} className="gap-2 font-bold rounded-lg cursor-pointer focus:bg-[#00FFA3]">
                      <Gift size={16} strokeWidth={2.5} /> {t.ref_title}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  data-testid="logout-btn"
                  onClick={async () => { await logout(); toast.success(t.auth_bye); }}
                  aria-label={t.nav_logout}
                  className="border-[2px] border-black rounded-full p-1.5 bg-white shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                >
                  <LogOut size={16} strokeWidth={2.5} />
                </button>
              </>
            ) : (
              <button
                data-testid="nav-login-btn"
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-1.5 bg-black text-white border-[2px] border-black rounded-full px-4 py-1.5 font-bold uppercase text-xs tracking-wide shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
              >
                <UserIcon size={15} strokeWidth={2.5} /> {t.nav_login}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* LOW BALANCE nudge — só quando resta 1 crédito, discreto e dispensável */}
      {user?.billing_enabled && user?.credits === 1 && !lowDismiss && (
        <div data-testid="low-balance-banner" className="w-full bg-[#FFD500] border-b-[3px] border-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
            <p className="text-sm font-bold">{t.low_balance}</p>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid="low-balance-buy"
                onClick={() => { setBuyReason(null); setShowBuy(true); }}
                className="bg-black text-white rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-[2px] border-black transition-colors hover:bg-[#FF3366]"
              >
                {t.low_balance_cta}
              </button>
              <button
                data-testid="low-balance-dismiss"
                onClick={() => setLowDismiss(true)}
                aria-label="fechar"
                className="border-[2px] border-black rounded-full w-6 h-6 flex items-center justify-center bg-white text-xs font-bold leading-none"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 pt-10 pb-6">
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block bg-[#FFD500] border-[3px] border-black rounded-full px-4 py-1.5 shadow-brutal mb-5 rotate-[-2deg]">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.1em]">
                {t.tagline}
              </span>
            </div>
            <h1
              className="font-display font-bold uppercase tracking-tighter leading-[0.88] mb-4"
              style={{ fontSize: "clamp(3rem, 12vw, 6.5rem)" }}
            >
              {t.hero_1} <br />
              <span className="text-[#FF3366]">{t.hero_2}</span>
            </h1>
            <p className="text-lg sm:text-xl font-medium text-[#333] max-w-2xl leading-relaxed">
              {t.hero_sub}
            </p>

            <div
              data-testid="psych-badge"
              className="mt-5 inline-flex items-center gap-2 bg-white border-[3px] border-black rounded-full px-4 py-2 shadow-brutal max-w-full"
            >
              <Brain size={18} strokeWidth={2.5} className="text-[#BD00FF] shrink-0" />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.06em]">
                {t.psych_badge}
              </span>
            </div>
          </motion.div>
        </div>

        {/* MODE SELECTOR */}
        <div data-testid="mode-selector" className="mt-8 grid grid-cols-2 gap-3">
          {[
            { id: "send", label: t.mode_send, emoji: "✍️" },
            { id: "received", label: t.mode_received, emoji: "📩" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`mode-${m.id}`}
              onClick={() => { setMode(m.id); setResult(null); }}
              className={`border-[3px] border-black rounded-xl px-3 py-3 font-display font-bold uppercase tracking-tight text-sm sm:text-lg shadow-brutal transition-[transform,box-shadow,background-color] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[3px] hover:translate-y-[3px] ${
                mode === m.id ? "bg-[#FFD500]" : "bg-white"
              }`}
            >
              {m.emoji} {m.label}
            </button>
          ))}
        </div>

        {/* INPUT CARD */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-4 bg-white border-[4px] border-black rounded-2xl shadow-brutal-lg p-4 sm:p-6"
        >
          <textarea
            data-testid="message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={mode === "send" ? t.ph_send : t.ph_received}
            rows={4}
            maxLength={2000}
            className="w-full resize-none bg-[#F4F0E6] border-[3px] border-black rounded-xl p-4 text-lg font-medium placeholder:text-[#888] focus:outline-none focus:ring-4 focus:ring-[#BD00FF] focus:ring-offset-0"
          />

          <p data-testid="privacy-note" className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#666]">
            <Lock size={13} strokeWidth={2.5} /> {t.privacy_note}
          </p>

          {/* Contexto no grátis — deixa a análise personalizada */}
          <div data-testid="context-section" className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">
                {t.q_rel}
              </p>
              <input
                type="text"
                data-testid="relationship-input"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                placeholder={t.rel_ph}
                className="w-full bg-white border-[2px] border-black rounded-xl px-3 py-2.5 text-sm font-medium placeholder:text-[#999] focus:outline-none focus:ring-4 focus:ring-black"
              />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">
                {t.q_status}
              </p>
              <textarea
                rows={2}
                data-testid="context-input"
                value={relStatus}
                onChange={(e) => setRelStatus(e.target.value)}
                placeholder={t.context_ph}
                className="w-full resize-none bg-white border-[2px] border-black rounded-xl px-3 py-2.5 text-sm font-medium placeholder:text-[#999] focus:outline-none focus:ring-4 focus:ring-black"
              />
            </div>
          </div>

          {/* No filter + analyze */}
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div
              className={`flex items-center gap-3 border-[3px] border-black rounded-xl px-4 py-3 ${
                noFilter ? "bg-[#FF3366]" : "bg-[#F4F0E6]"
              }`}
            >
              <Flame size={20} strokeWidth={2.5} className={noFilter ? "text-white" : "text-black"} />
              <span
                className={`font-bold uppercase tracking-[0.06em] text-sm ${
                  noFilter ? "text-white" : "text-black"
                }`}
              >
                {t.no_filter}
              </span>
              <Switch
                data-testid="no-filter-toggle"
                aria-label="Ativar modo sem filtro"
                checked={noFilter}
                onCheckedChange={toggleFilter}
                className="data-[state=checked]:bg-black data-[state=unchecked]:bg-white border-[2px] border-black"
              />
            </div>

            <button
              data-testid="analyze-btn"
              onClick={analyze}
              disabled={loading}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00FFA3] border-[3px] border-black rounded-xl px-6 py-4 font-display font-bold uppercase tracking-tight text-xl sm:text-2xl shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[3px_3px_0px_0px_#000] hover:translate-x-[3px] hover:translate-y-[3px] active:shadow-none active:translate-x-[6px] active:translate-y-[6px] disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 size={24} strokeWidth={2.5} className="animate-spin" /> {t.judging}
                </>
              ) : (
                <>
                  <Zap size={24} strokeWidth={2.5} fill="currentColor" /> {t.analyze}
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* ANALISE SUA CONVERSA */}
        <button
          data-testid="conversation-btn"
          onClick={() => (user ? setShowConvo(true) : setShowAuth(true))}
          className="mt-4 w-full text-left flex items-center gap-4 bg-[#BD00FF] text-white border-[4px] border-black rounded-2xl px-5 py-4 shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[3px_3px_0px_0px_#000] hover:translate-x-[3px] hover:translate-y-[3px]"
        >
          <span className="shrink-0 bg-white text-black border-[3px] border-black rounded-xl p-2">
            <MessagesSquare size={26} strokeWidth={2.5} />
          </span>
          <span className="flex flex-col">
            <span className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl leading-none">{t.cv_btn}</span>
            <span className="text-sm font-medium text-white/90 mt-1">{t.cv_btn_sub}</span>
          </span>
        </button>

        {/* RESULT */}
        <div ref={resultRef} className="scroll-mt-6">
          <AnimatePresence>
            {result && (
              <div className="mt-8">
                <VerdictCard
                  result={result}
                  noFilter={noFilter}
                  mode={result.mode}
                  onShare={handleShare}
                  onCopy={handleCopy}
                  onAnother={handleAnother}
                  onReply3={handleReply3}
                  reply3ing={reply3ing}
                  replies={replies}
                  onJudgeMe={handleJudgeMe}
                  judging={judging}
                  observations={observations}
                  onCopyText={copyText}
                />

                {result.depth === "summary" && (
                  <div data-testid="deep-upsell" className="mt-6 border-[4px] border-black rounded-2xl bg-[#BD00FF] shadow-brutal-lg p-5 sm:p-6">
                    <p className="font-display font-bold uppercase text-white text-2xl tracking-tight leading-none mb-1">
                      {t.deep_title}
                    </p>
                    <p className="text-white/90 font-medium text-sm mb-4">
                      {t.deep_sub}
                    </p>

                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-white mb-2">{t.deep_extra_label}</p>
                    <textarea data-testid="context-extra" value={extraNote} onChange={(e) => setExtraNote(e.target.value)}
                      placeholder={t.deep_extra_ph}
                      rows={2} maxLength={500}
                      className="w-full resize-none bg-white border-[2px] border-black rounded-xl p-3 text-base font-medium placeholder:text-[#999] focus:outline-none focus:ring-4 focus:ring-black" />

                    <button data-testid="deep-analyze-btn" onClick={runDeep} disabled={deepLoading}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#00FFA3] border-[3px] border-black rounded-xl px-4 py-3.5 font-display font-bold uppercase text-lg tracking-tight shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] disabled:opacity-70">
                      {deepLoading ? <><Loader2 size={20} className="animate-spin" /> {t.deep_loading}</> : <><Sparkles size={20} strokeWidth={2.5} /> {t.deep_btn}</>}
                    </button>
                    {user?.billing_enabled && (
                    <p className="text-center text-white/80 text-xs font-semibold mt-2">
                      {t.you_have} {user?.credits ?? 0} {t.credits_word}
                    </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* PSYCHOLOGY / THERAPEUTIC SUPPORT */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-6">
        <div className="border-[4px] border-black rounded-2xl shadow-brutal-lg bg-[#BD00FF] p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 bg-white border-[3px] border-black rounded-full px-4 py-1.5 shadow-brutal mb-5">
            <Brain size={18} strokeWidth={2.5} className="text-[#BD00FF]" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.08em]">
              {t.psych_pill}
            </span>
          </div>
          <h2 className="font-display font-bold uppercase tracking-tight text-white text-3xl sm:text-5xl leading-[0.95] mb-4">
            {t.psych_h}
          </h2>
          <p className="text-white font-semibold text-lg sm:text-xl leading-relaxed max-w-2xl">
            {t.psych_p}
          </p>
          <p className="text-white font-semibold text-lg sm:text-xl leading-relaxed max-w-2xl mt-4">
            {t.psych_p2}
          </p>
          <p className="text-white/80 font-medium text-sm mt-5 leading-relaxed max-w-2xl">
            {t.psych_disc}
          </p>
        </div>
      </section>

      {/* MARQUEE separator */}
      <div className="w-full bg-black text-[#00FFA3] border-y-[3px] border-black py-3 overflow-hidden my-6">
        <div className="marquee-track font-display font-bold uppercase tracking-tight text-xl">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="mx-6">
              {t.marquee}
            </span>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <footer className="border-t-[3px] border-black bg-[#F4F0E6] py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="font-display font-bold uppercase tracking-tight text-lg">📵 Mando ou Não Mando?</p>
          <p className="text-sm font-medium text-[#444] mt-1">
            {t.foot_priv1}
          </p>
          <p className="text-xs font-medium text-[#666] mt-3 max-w-xl mx-auto leading-relaxed">
            {t.foot_lgpd}
          </p>
          <p className="text-xs font-medium text-[#666] mt-3 max-w-xl mx-auto leading-relaxed">
            {t.foot_psych}
          </p>
          <div className="mt-4 flex items-center justify-center gap-4 text-xs font-bold uppercase tracking-[0.08em]">
            <a data-testid="footer-privacy-link" href="/privacidade.html" className="underline hover:text-[#BD00FF]">
              {t.foot_privacy_link}
            </a>
            <span className="text-[#999]">·</span>
            <a data-testid="footer-terms-link" href="/termos.html" className="underline hover:text-[#BD00FF]">
              {t.foot_terms_link}
            </a>
          </div>
        </div>
      </footer>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showBuy && <BuyCreditsModal reason={buyReason} onClose={() => setShowBuy(false)} />}
      {showHistory && <HistoryModal token={typeof window !== "undefined" ? localStorage.getItem("mnm_token") : null} onClose={() => setShowHistory(false)} />}
      {showPatterns && <PatternsModal token={typeof window !== "undefined" ? localStorage.getItem("mnm_token") : null} onClose={() => setShowPatterns(false)} />}
      {showDaily && <DailyChallengeModal token={typeof window !== "undefined" ? localStorage.getItem("mnm_token") : null} onClose={() => setShowDaily(false)} />}
      {showReferral && <ReferralModal token={typeof window !== "undefined" ? localStorage.getItem("mnm_token") : null} onClose={() => setShowReferral(false)} />}
      {showConvo && (
        <ConversationModal
          noFilter={noFilter}
          onToggleFilter={toggleFilter}
          onClose={() => setShowConvo(false)}
          onNeedCredits={() => { setShowConvo(false); setBuyReason("limit"); setShowBuy(true); refreshUser(); }}
          onDone={refreshUser}
        />
      )}
      {result && <ShareCard ref={shareCardRef} result={result} verdictLabel={t[`verdict_${result.verdict}`]} />}
    </div>
  );
}

export default App;
